// Lexical segmentation of an expression's source, shared by the reference
// discovery in `references.ts` and the textual rewrites in `expressionRewrite.ts`.
// Pure string operations, so the browser-safe `./references` subpath can use it.
// Reserved words after which a `/` opens a regex literal although they end in
// a word character; any other word ends an operand and makes the `/` a
// division. The contextual `of` is handled separately: it is a keyword only
// inside a `for (…)` header and an ordinary identifier elsewhere.
const regexPrecedingKeywords = new Set([
    "return",
    "typeof",
    "instanceof",
    "in",
    "new",
    "delete",
    "void",
    "throw",
    "case",
    "do",
    "else",
    "yield",
    "await",
    "break",
    "continue",
    "debugger",
]);
// A `)` closing one of these headers is followed by a statement, not an operator.
const controlHeaderKeywords = new Set(["if", "while", "for", "with"]);
// A `{` after one of these opens a statement block, not an object literal.
const blockPrecedingKeywords = new Set(["else", "do", "try", "finally"]);
const forKeyword = new Set(["for"]);
const ofKeyword = new Set(["of"]);
/**
 * Split an expression's source into the code a reference token can be read
 * from and the segments it must not be read from: string literals, template
 * literals (their interpolations are code again) and regex literals, and comments.
 * A lexical scan rather than a quote-pairing regex, so a quote inside a regex
 * literal or a comment cannot swallow the code that follows it.
 */
export function scanSourceSegments(expr) {
    const segments = [];
    // Offsets of every non-code segment, for the look-back in `regexAllowedAt`.
    const skipped = [];
    // Brace depth at which each open template interpolation's `}` closes it.
    const interpolationDepths = [];
    let braceDepth = 0;
    // What each open `{` in code opened, and what each `}` closed, by offset.
    const openBraceKinds = [];
    const closedBraceKindAt = new Map();
    let codeStart = 0;
    const pushSkipped = (kind, start, end) => {
        if (start > codeStart) {
            segments.push({ kind: "code", text: expr.slice(codeStart, start) });
        }
        segments.push({ kind, text: expr.slice(start, end) });
        skipped.push({ kind, start, end });
        codeStart = end;
    };
    const pushLiteral = (start, end) => pushSkipped("literal", start, end);
    const pushComment = (start, end) => pushSkipped("comment", start, end);
    /** Index after the closing `quote`, honouring backslash escapes. */
    const endOfQuoted = (start, quote) => {
        let j = start + 1;
        while (j < expr.length && expr[j] !== quote) {
            j += expr[j] === "\\" ? 2 : 1;
        }
        return Math.min(j + 1, expr.length);
    };
    /** Index after the closing `/` and flags; `/` inside a class does not close. */
    const endOfRegex = (start) => {
        let j = start + 1;
        let inClass = false;
        while (j < expr.length) {
            const c = expr[j];
            if (c === "\\") {
                j += 2;
                continue;
            }
            j += 1;
            if (c === "[")
                inClass = true;
            else if (c === "]")
                inClass = false;
            else if (c === "/" && !inClass)
                break;
        }
        while (j < expr.length && /[a-z]/i.test(expr[j]))
            j += 1;
        return j;
    };
    /**
     * Consume a template quasi from `start` (the opening backtick or the `}`
     * closing an interpolation) up to the closing backtick, or up to and
     * including a `${`, which opens an interpolation. Returns the index to
     * resume the code scan at.
     */
    const consumeTemplateQuasi = (start) => {
        let j = start + 1;
        while (j < expr.length) {
            const c = expr[j];
            if (c === "\\") {
                j += 2;
                continue;
            }
            if (c === "`") {
                pushLiteral(start, j + 1);
                return j + 1;
            }
            if (c === "$" && expr[j + 1] === "{") {
                pushLiteral(start, j + 2);
                interpolationDepths.push(braceDepth);
                return j + 2;
            }
            j += 1;
        }
        pushLiteral(start, expr.length);
        return expr.length;
    };
    /**
     * Index of the last significant character before `from`, skipping whitespace
     * and comments, or -1. Yields the skipped segment the index falls into, if any.
     */
    const significantBefore = (from) => {
        let k = from - 1;
        for (let s = skipped.length - 1;;) {
            while (k >= 0 && /\s/.test(expr[k]))
                k -= 1;
            if (k < 0)
                return { k, segment: undefined };
            while (s >= 0 && skipped[s].start > k)
                s -= 1;
            if (s < 0 || skipped[s].end <= k)
                return { k, segment: undefined };
            if (skipped[s].kind === "literal")
                return { k, segment: skipped[s] };
            k = skipped[s].start - 1;
        }
    };
    /**
     * The keyword ending at `k`, or undefined when the word there is not one of
     * `keywords` or is a member name (`obj.return`), which is an identifier.
     */
    const keywordEndingAt = (k, keywords) => {
        let wordStart = k;
        while (wordStart > 0 && /[\w$]/.test(expr[wordStart - 1]))
            wordStart -= 1;
        const word = expr.slice(wordStart, k + 1);
        if (!keywords.has(word))
            return undefined;
        const { k: before, segment } = significantBefore(wordStart);
        return before >= 0 && segment === undefined && expr[before] === "."
            ? undefined
            : word;
    };
    /** Whether `pos` lies inside a `for (…)` header — its nearest unclosed `(` follows `for`. */
    const insideForHeader = (pos) => {
        let depth = 0;
        let cursor = pos;
        for (;;) {
            const { k, segment } = significantBefore(cursor);
            if (k < 0)
                return false;
            if (segment !== undefined) {
                cursor = segment.start;
                continue;
            }
            if (expr[k] === ")")
                depth += 1;
            else if (expr[k] === "(") {
                if (depth === 0) {
                    const before = significantBefore(k);
                    return (before.k >= 0 &&
                        before.segment === undefined &&
                        /[\w$]/.test(expr[before.k]) &&
                        keywordEndingAt(before.k, forKeyword) !== undefined);
                }
                depth -= 1;
            }
            cursor = k;
        }
    };
    /** Index of the `(` matching the `)` at `close`, or -1. */
    const matchingOpenParen = (close) => {
        let depth = 1;
        let pos = close;
        for (;;) {
            const { k, segment } = significantBefore(pos);
            if (k < 0)
                return -1;
            if (segment !== undefined) {
                pos = segment.start;
                continue;
            }
            if (expr[k] === ")")
                depth += 1;
            else if (expr[k] === "(") {
                depth -= 1;
                if (depth === 0)
                    return k;
            }
            pos = k;
        }
    };
    /** The identifier-like word ending at `k` (a word character), with its start. */
    const wordEndingAt = (k) => {
        let start = k;
        while (start > 0 && /[\w$]/.test(expr[start - 1]))
            start -= 1;
        return { word: expr.slice(start, k + 1), start };
    };
    /** Whether the `)` at `close` ends an `if (…)`-style header. */
    const closesControlHeader = (close) => {
        const open = matchingOpenParen(close);
        if (open < 0)
            return false;
        const before = significantBefore(open);
        return (before.k >= 0 &&
            before.segment === undefined &&
            /[\w$]/.test(expr[before.k]) &&
            keywordEndingAt(before.k, controlHeaderKeywords) !== undefined);
    };
    /**
     * Whether a `function`/`class` keyword at `keywordStart` begins an expression
     * (operand position: after an operator, an opening bracket, `return` and its
     * kin, an interpolation opener, or at the start of the source, which the
     * evaluator places in return position) rather than a declaration.
     */
    const isExpressionPosition = (keywordStart) => {
        const { k, segment } = significantBefore(keywordStart);
        if (k < 0)
            return true;
        if (segment !== undefined)
            return opensInterpolation(segment);
        const c = expr[k];
        if (c === ";" || c === "{" || c === "}" || c === ")")
            return false;
        if (/[\w$]/.test(c)) {
            return keywordEndingAt(k, regexPrecedingKeywords) !== undefined;
        }
        return true;
    };
    /**
     * The kind of a `{` at `open` that is the body of a function or class —
     * "expression" when that function/class is an expression, so its `}`
     * completes an operand, "block" for a declaration — or undefined when the
     * `{` is not such a body. `open` follows either the `)` of a
     * `function [name](…)` parameter list, or `class [name] [extends <name>]`.
     */
    const bodyKindOpenedAt = (open) => {
        const kindAt = (keywordStart) => isExpressionPosition(keywordStart) ? "expression" : "block";
        const { k, segment } = significantBefore(open);
        if (k < 0 || segment !== undefined)
            return undefined;
        if (expr[k] === ")") {
            const paren = matchingOpenParen(k);
            if (paren < 0)
                return undefined;
            let before = significantBefore(paren);
            if (before.k < 0 ||
                before.segment !== undefined ||
                !/[\w$]/.test(expr[before.k])) {
                return undefined;
            }
            let { word, start } = wordEndingAt(before.k);
            if (word !== "function") {
                // `function name(…)`: step over the name.
                before = significantBefore(start);
                if (before.k < 0 ||
                    before.segment !== undefined ||
                    !/[\w$]/.test(expr[before.k])) {
                    return undefined;
                }
                ({ word, start } = wordEndingAt(before.k));
                if (word !== "function")
                    return undefined;
            }
            return kindAt(start);
        }
        if (!/[\w$]/.test(expr[k]))
            return undefined;
        // `class [name] [extends <name>] {`: walk back over the words to `class`.
        let { word, start } = wordEndingAt(k);
        for (let steps = 0; steps < 4; steps += 1) {
            if (word === "class")
                return kindAt(start);
            const before = significantBefore(start);
            if (before.k < 0 ||
                before.segment !== undefined ||
                !/[\w$]/.test(expr[before.k])) {
                return undefined;
            }
            ({ word, start } = wordEndingAt(before.k));
        }
        return undefined;
    };
    // A `/` divides when the previous lexical token can end an operand: a
    // literal (string, template, regex or a number ending in a dot), a closing
    // bracket (unless it closes a control-flow header or a statement block —
    // an object literal's or a function/class expression body's `}` does end
    // one), a
    // postfix `++`/`--`, or a
    // word that is not a reserved word (a keyword-named member such as
    // `obj.return` is a word). Comments are skipped like whitespace. Anywhere
    // else it opens a regex literal.
    /** Whether a skipped segment is a template quasi ending in `${`. */
    const opensInterpolation = (segment) => segment.kind === "literal" &&
        expr.slice(segment.end - 2, segment.end) === "${";
    const regexAllowedAt = (slash) => {
        const { k, segment } = significantBefore(slash);
        if (k < 0)
            return true;
        // An interpolation's expression starts right after `${`.
        if (segment !== undefined)
            return opensInterpolation(segment);
        const c = expr[k];
        if (c === ")")
            return closesControlHeader(k);
        if (c === "]")
            return false;
        // A `}` ends an operand only when it completes an expression.
        if (c === "}")
            return closedBraceKindAt.get(k) !== "expression";
        if ((c === "+" || c === "-") && expr[k - 1] === c) {
            let m = k - 2;
            while (m >= 0 && /\s/.test(expr[m]))
                m -= 1;
            return m < 0 || !/[\w$)\]]/.test(expr[m]);
        }
        // A numeric literal may end in a dot (`4.`), which is an operand end.
        if (c === ".")
            return !/\d/.test(expr[k - 1] ?? "");
        if (!/[\w$]/.test(c))
            return true;
        if (keywordEndingAt(k, regexPrecedingKeywords) !== undefined)
            return true;
        // `of` opens the iterable of a `for (… of …)` header, which may be a regex.
        const of = keywordEndingAt(k, ofKeyword);
        return of !== undefined && insideForHeader(k - of.length + 1);
    };
    /**
     * Whether the `{` at `open` starts a statement block or completes an
     * expression when closed (an object literal, or the body of a function or
     * class expression), decided from the previous significant token: a block
     * follows a statement boundary (`;`, `{`, `}`, a `)`, `=>`, or `else`/`do`/
     * `try`/`finally`); an object literal follows an operator, an opening
     * bracket, `return`, or the start of the source, which the evaluator places
     * in return position.
     */
    const braceKindOpenedAt = (open) => {
        const body = bodyKindOpenedAt(open);
        if (body !== undefined)
            return body;
        const { k, segment } = significantBefore(open);
        if (k < 0)
            return "expression";
        if (segment !== undefined) {
            return opensInterpolation(segment) ? "expression" : "block";
        }
        const c = expr[k];
        if (c === ";" || c === "{")
            return "block";
        if (c === "}")
            return closedBraceKindAt.get(k) === "expression" ? "expression" : "block";
        if (c === ")")
            return "block";
        if (c === ">" && expr[k - 1] === "=")
            return "block";
        if (/[\w$]/.test(c)) {
            return keywordEndingAt(k, blockPrecedingKeywords) !== undefined
                ? "block"
                : "expression";
        }
        return "expression";
    };
    let i = 0;
    while (i < expr.length) {
        const c = expr[i];
        if (c === '"' || c === "'") {
            const end = endOfQuoted(i, c);
            pushLiteral(i, end);
            i = end;
        }
        else if (c === "`") {
            i = consumeTemplateQuasi(i);
        }
        else if (c === "/" && expr[i + 1] === "/") {
            const newline = expr.indexOf("\n", i);
            const end = newline < 0 ? expr.length : newline;
            pushComment(i, end);
            i = end;
        }
        else if (c === "/" && expr[i + 1] === "*") {
            const close = expr.indexOf("*/", i + 2);
            const end = close < 0 ? expr.length : close + 2;
            pushComment(i, end);
            i = end;
        }
        else if (c === "/" && regexAllowedAt(i)) {
            const end = endOfRegex(i);
            pushLiteral(i, end);
            i = end;
        }
        else if (c === "{") {
            braceDepth += 1;
            openBraceKinds.push(braceKindOpenedAt(i));
            i += 1;
        }
        else if (c === "}" &&
            interpolationDepths.length > 0 &&
            interpolationDepths[interpolationDepths.length - 1] === braceDepth) {
            interpolationDepths.pop();
            i = consumeTemplateQuasi(i);
        }
        else if (c === "}") {
            braceDepth -= 1;
            closedBraceKindAt.set(i, openBraceKinds.pop() ?? "block");
            i += 1;
        }
        else {
            i += 1;
        }
    }
    if (codeStart < expr.length) {
        segments.push({ kind: "code", text: expr.slice(codeStart) });
    }
    return segments;
}
/**
 * Apply a textual rewrite to the code segments of `expr` only, so a reference
 * token spelled inside a string, template or regex literal or a comment (a text
 * fallback, say) survives the rewrite unchanged — the same segmentation the
 * reference discovery reads from.
 */
export function replaceOutsideLiterals(expr, replaceCode) {
    return scanSourceSegments(expr)
        .map(s => (s.kind === "code" ? replaceCode(s.text) : s.text))
        .join("");
}
//# sourceMappingURL=sourceSegments.js.map